import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { statsApi, type DashboardStats, type RecentInquiry } from "@/lib/api";
import { formatDate } from "@/lib/date";
import { getServiceBadge, getStatusBadge, maskName } from "@/lib/constants";

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    equipmentCount: 0,
    galleryCount: 0,
    inquiryCount: 0,
    inquiryReceived: 0,
    inquiryProcessing: 0,
    inquiryDone: 0,
    recentInquiries: [],
  });
  const [recentInquiries, setRecentInquiries] = useState<RecentInquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await statsApi.get();
      setStats(data);
      setRecentInquiries(data.recentInquiries || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "데이터를 불러오지 못했습니다.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 w-8 h-8 flex items-center justify-center"></i>
          <span className="text-foreground-500 text-sm">대시보드 로딩 중...</span>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-background-50 pt-20">
      {/* Top bar */}
      <div className="bg-background-50 border-b border-background-200/60">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="w-9 h-9 rounded-md border border-background-200 bg-background-50 hover:bg-background-100 flex items-center justify-center cursor-pointer transition-colors"
              title="홈으로"
            >
              <i className="ri-home-3-line w-4 h-4 flex items-center justify-center text-foreground-600"></i>
            </button>
            <h1 className="font-heading text-xl font-bold text-foreground-950">관리자 대시보드</h1>
          </div>
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line w-4 h-4 flex items-center justify-center"></i>
            새로고침
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-8">
        {error && (
          <div className="mb-6 p-4 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-3">
            <i className="ri-error-warning-line text-accent-600 w-5 h-5 flex items-center justify-center mt-0.5"></i>
            <div className="flex-1">
              <p className="text-accent-800 text-sm">{error}</p>
            </div>
            <button
              onClick={fetchData}
              className="px-3 py-1.5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-xs font-medium cursor-pointer whitespace-nowrap transition-colors"
            >
              다시 시도
            </button>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon="ri-tools-line"
            label="보유 장비"
            value={stats.equipmentCount}
            unit="대"
            color="primary"
            onClick={() => navigate("/admin/equipment")}
          />
          <StatCard
            icon="ri-image-line"
            label="갤러리 이미지"
            value={stats.galleryCount}
            unit="장"
            color="secondary"
            onClick={() => navigate("/admin/gallery")}
          />
          <StatCard
            icon="ri-chat-1-line"
            label="전체 문의"
            value={stats.inquiryCount}
            unit="건"
            color="accent"
          />
          <div className="col-span-2 lg:col-span-1 bg-background-50 rounded-lg border border-background-200/60 p-5 flex flex-col gap-2">
            <span className="text-xs text-foreground-500 font-medium tracking-wide uppercase">문의 상태</span>
            <div className="flex items-center gap-3 mt-1">
              <div className="flex flex-col items-center flex-1">
                <span className="text-2xl font-bold text-foreground-950">{stats.inquiryReceived}</span>
                <span className="text-[11px] text-foreground-400 mt-0.5">접수</span>
              </div>
              <div className="w-px h-8 bg-background-200/60"></div>
              <div className="flex flex-col items-center flex-1">
                <span className="text-2xl font-bold text-accent-700">{stats.inquiryProcessing}</span>
                <span className="text-[11px] text-foreground-400 mt-0.5">처리중</span>
              </div>
              <div className="w-px h-8 bg-background-200/60"></div>
              <div className="flex flex-col items-center flex-1">
                <span className="text-2xl font-bold text-secondary-700">{stats.inquiryDone}</span>
                <span className="text-[11px] text-foreground-400 mt-0.5">완료</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <QuickLinkCard
            icon="ri-tools-line"
            title="장비 관리"
            description="보유 장비 등록, 수정, 삭제"
            href="/admin/equipment"
            navigate={navigate}
          />
          <QuickLinkCard
            icon="ri-image-line"
            title="갤러리 관리"
            description="작업 이미지 업로드 및 관리"
            href="/admin/gallery"
            navigate={navigate}
          />
          <QuickLinkCard
            icon="ri-chat-1-line"
            title="문의 게시판 관리"
            description="고객 문의 확인, 상태 변경, 삭제"
            href="/admin/inquiries"
            navigate={navigate}
          />
        </div>

        {/* Recent Inquiries Table */}
        <div className="bg-background-50 rounded-lg border border-background-200/60 overflow-hidden">
          <div className="px-5 py-4 border-b border-background-200/60 flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-foreground-950">최근 문의</h2>
            <button
              onClick={() => navigate("/admin/inquiries")}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap transition-colors"
            >
              전체보기
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center ml-1"></i>
            </button>
          </div>

          {recentInquiries.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <i className="ri-inbox-line text-3xl text-foreground-300 w-8 h-8 flex items-center justify-center mx-auto mb-2"></i>
              <p className="text-sm text-foreground-500">아직 등록된 문의가 없습니다.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-background-200/60 bg-background-100/40">
                    <th className="text-left px-5 py-3 text-xs font-medium text-foreground-500 tracking-wide uppercase">서비스</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-foreground-500 tracking-wide uppercase">작성자</th>
                    <th className="text-center px-5 py-3 text-xs font-medium text-foreground-500 tracking-wide uppercase">상태</th>
                    <th className="text-right px-5 py-3 text-xs font-medium text-foreground-500 tracking-wide uppercase">날짜</th>
                  </tr>
                </thead>
                <tbody>
                  {recentInquiries.map((inq) => (
                    <tr
                      key={inq.id}
                      onClick={() => navigate("/admin/inquiries")}
                      className="border-b border-background-100 hover:bg-background-50/60 cursor-pointer transition-colors"
                    >
                      <td className="px-5 py-3.5">
                        <span
                          className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getServiceBadge(inq.service)}`}
                        >
                          {inq.service}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-foreground-600">{maskName(inq.name)}</td>
                      <td className="px-5 py-3.5 text-center">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${getStatusBadge(inq.status)}`}
                        >
                          {inq.status || "접수"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right text-xs text-foreground-400 whitespace-nowrap">{formatDate(inq.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function StatCard({
  icon,
  label,
  value,
  unit,
  color,
  onClick,
}: {
  icon: string;
  label: string;
  value: number;
  unit: string;
  color: "primary" | "secondary" | "accent";
  onClick?: () => void;
}) {
  const iconBg: Record<string, string> = {
    primary: "bg-primary-100 text-primary-600",
    secondary: "bg-secondary-100 text-secondary-600",
    accent: "bg-accent-100 text-accent-600",
  };

  return (
    <div
      onClick={onClick}
      className={`bg-background-50 rounded-lg border border-background-200/60 p-5 flex items-start gap-4 ${onClick ? "cursor-pointer hover:border-primary-300/60 transition-colors" : ""}`}
    >
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg[color]}`}>
        <i className={`${icon} w-5 h-5 flex items-center justify-center`}></i>
      </div>
      <div className="flex flex-col">
        <span className="text-xs text-foreground-500 font-medium tracking-wide uppercase">{label}</span>
        <div className="flex items-baseline gap-1 mt-0.5">
          <span className="text-2xl font-bold text-foreground-950">{value}</span>
          <span className="text-sm text-foreground-400">{unit}</span>
        </div>
      </div>
    </div>
  );
}

function QuickLinkCard({
  icon,
  title,
  description,
  href,
  navigate,
}: {
  icon: string;
  title: string;
  description: string;
  href: string;
  navigate: ReturnType<typeof useNavigate>;
}) {
  return (
    <div
      onClick={() => navigate(href)}
      className="bg-background-50 rounded-lg border border-background-200/60 p-5 cursor-pointer hover:border-primary-300/60 transition-colors group"
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 rounded-md bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors">
          <i className={`${icon} w-4 h-4 flex items-center justify-center text-primary-600`}></i>
        </div>
        <h3 className="font-heading text-sm font-bold text-foreground-950">{title}</h3>
      </div>
      <p className="text-xs text-foreground-500 leading-relaxed">{description}</p>
    </div>
  );
}